/**
 * Format: Product
 * -----------------------------------------------------------------------------
 * Formats Shopify storefront API product responses.
 *
 */
import { formatImages } from '~/format/format-image'
import { formatMedia } from '~/format/format-media'
import { formatColorOptions, formatOptions } from '~/format/format-option'
import { formatProductPriceRange } from '~/format/format-price'
import { formatVariants } from '~/format/format-variant'
import { decodeApiId, transformEdges } from '~/helpers/graphql'

/**
 * Format product.
 * - https://shopify.dev/docs/api/storefront/2023-07/objects/Product
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatProduct(response) {
  const product = response.product

  const formattedProduct = {
    available: product.available,
    compareAtPrice: formatProductPriceRange(product.compareAtPriceRange),
    description: product.descriptionHtml,
    giftCard: product.type === 'Gift card' ? true : product.giftCard,
    handle: product.handle,
    id: decodeApiId(product.id),
    images: formatImages(product),
    media: formatMedia(product),
    price: formatProductPriceRange(product.priceRange),
    tags: product.tags,
    title: product.title,
    type: product.type,
    url: `//${location.host}${cnvs.routes.normalisedRoot}products/${product.handle}`,
    variants: formatVariants(product),
    vendor: product.vendor,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  const featuredImages = formatImages(product, transformEdges(product.featuredImages))

  formattedProduct.featuredImage = featuredImages.length
    ? featuredImages[0]
    : featuredImages

  formattedProduct.secondaryImage = featuredImages.length > 1
    ? featuredImages[1]
    : {}

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants.every((variant) => {
    return variant.title.trim().toLowerCase() === 'default title'
  })

  formattedProduct.options = formatOptions(product, formattedProduct.variants)
  formattedProduct.colorOptions = formatColorOptions(product, formattedProduct.variants)

  formattedProduct.hoverImages = formattedProduct.media
    .filter((image) => image.variant)
    .map((media) => media.image)

  return formattedProduct
}
